import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Loader2, PauseCircle, PlayCircle, Trash2, XCircle } from 'lucide-react';
import ConfirmDialog from './ConfirmDialog';
import StepUpDialog from './StepUpDialog';
import PriceChangeTable, { priceRows, resultRows } from './PriceChangeTable';
import { makeCommandId } from '../components/InvoiceApprovalReview';
import { invoiceCommand, removeUpload } from './api';

const big = 'inline-flex items-center justify-center gap-2 min-h-[52px] px-5 rounded-xl font-medium disabled:opacity-50 motion-organic';

/** Approve (with a preview of price changes), Reject, Hold / Release hold and the demo-only Remove upload. */
export default function ApprovePanel({ invoice, openTasks, extractionTask = false, items, reloadItems, onChanged, canRemove, onRemoved }) {
  const [dialog, setDialog] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState(null);
  const [before, setBefore] = useState(null);
  const cmd = useRef({});
  const state = invoice.state;
  const onHold = !!invoice.hold;
  const rows = priceRows(invoice, items);

  const commandFor = (action) => {
    if (!cmd.current[action]) cmd.current[action] = makeCommandId(`inv-${action}`);
    return cmd.current[action];
  };
  const close = () => { setDialog(null); setReason(''); setError(null); };

  // Approving writes prices and re-costs dishes, then the page reloads: that can take a while. The dialog closes
  // as soon as it is confirmed and the panel shows "Posting prices…" until it is done, so the person is not
  // held on a popup. A failure shows on the panel with Approve available again.
  const send = async (action, extra = {}) => {
    const inBackground = action === 'approve';
    setError(null);
    if (inBackground) { setDialog(null); setReason(''); setPosting(true); } else setBusy(true);
    try {
      await invoiceCommand(invoice.id, action, { command_id: commandFor(action), ...extra });
      cmd.current[action] = null;
      if (action === 'approve') {
        setBefore(Object.fromEntries(rows.map((r) => [r.itemId, { price: r.oldPrice, unit: r.oldUnit }])));
        await reloadItems(rows.map((r) => r.itemId));
      }
      if (!inBackground) close();
      await onChanged();
    } catch (e) {
      setError(e.message);
    } finally {
      if (inBackground) setPosting(false); else setBusy(false);
    }
  };

  // An approved invoice also undoes the prices it set: that needs a second check (StepUpDialog), not one click.
  const approved = state === 'approved';
  const remove = async () => {
    if (approved) { setError(null); setDialog('remove-step-up'); return; }
    setBusy(true);
    try { await removeUpload(invoice.id); close(); onRemoved(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <section className="rounded-2xl border border-surface-border bg-surface p-5 space-y-4" data-testid="approve-panel">
      {state === 'needs_review' && (
        <p className="text-ink" data-testid="approve-blocked">
          <strong>{openTasks}</strong> line{openTasks === 1 ? '' : 's'} still need{openTasks === 1 ? 's' : ''} a decision before this invoice can be approved.
        </p>
      )}
      {extractionTask && (
        <p className="rounded-xl bg-accent-50 border border-accent-300 px-4 py-3 text-accent-800" data-testid="extraction-task-notice">
          Some figures on this invoice could not be read with confidence. Correct them on the{' '}
          <Link to="/review" className="underline font-medium" data-testid="extraction-task-link">full review screen</Link>, then come back here.
        </p>
      )}
      {posting && (
        <p className="flex items-center gap-2 rounded-xl bg-primary-50 border border-primary-200 px-4 py-3 text-primary-700"
          role="status" data-testid="approve-pending">
          <Loader2 className="w-5 h-5 animate-spin shrink-0" aria-hidden="true" />
          Posting prices… you can keep working; this updates when it is done.
        </p>
      )}
      {state === 'awaiting_approval' && !onHold && !posting && (
        <p className="text-ink" data-testid="approve-ready">Every line is sorted. Check the price changes, then approve.</p>
      )}
      {onHold && (
        <p className="rounded-xl bg-warn/10 border border-warn/40 px-4 py-3 text-ink" data-testid="hold-banner">
          On hold{invoice.hold?.reason ? `: ${invoice.hold.reason}` : ''}
        </p>
      )}
      {(state === 'approved' || state === 'auto') && (
        <div className="space-y-3" data-testid="approve-result">
          <p className="flex items-center gap-2 text-primary-700 font-medium"><CheckCircle2 className="w-5 h-5" /> Posted — this invoice's prices are recorded.</p>
          <PriceChangeTable rows={resultRows(invoice, items, before || {})} testid="approve-result-table" />
          <Link to="/pricing/impact" data-testid="approve-result-costing-link" className={`${big} bg-primary-500 text-surface hover:bg-primary-600 w-full sm:w-auto`}>
            See what this does to dish costs
          </Link>
        </div>
      )}
      {state === 'rejected' && (
        <p className="text-ink-muted" data-testid="rejected-banner">Rejected{invoice.rejection?.reason ? `: ${invoice.rejection.reason}` : ''}. No prices were written.</p>
      )}

      <div className="flex flex-col sm:flex-row flex-wrap gap-3">
        {state === 'awaiting_approval' && !onHold && (
          <button type="button" disabled={posting} className={`${big} bg-primary-500 text-surface hover:bg-primary-600`} data-testid="approve-btn" onClick={() => setDialog('approve')}>
            <CheckCircle2 className="w-5 h-5" /> Approve
          </button>
        )}
        {state === 'needs_review' && (
          <button type="button" disabled className={`${big} bg-primary-500 text-surface`} data-testid="approve-btn-disabled">Approve</button>
        )}
        {state === 'awaiting_approval' && !onHold && (
          <button type="button" disabled={posting} className={`${big} border border-warn/60 text-ink hover:bg-warn/10`} data-testid="hold-btn" onClick={() => setDialog('hold')}>
            <PauseCircle className="w-5 h-5" /> Hold
          </button>
        )}
        {onHold && (
          <button type="button" disabled={busy} className={`${big} border border-warn/60 text-ink hover:bg-warn/10`} data-testid="release-hold-btn" onClick={() => send('release-hold')}>
            <PlayCircle className="w-5 h-5" /> Release hold
          </button>
        )}
        {(state === 'awaiting_approval' || state === 'needs_review') && (
          <button type="button" disabled={posting} className={`${big} border border-danger/40 text-danger hover:bg-danger/5`} data-testid="reject-btn" onClick={() => setDialog('reject')}>
            <XCircle className="w-5 h-5" /> Reject
          </button>
        )}
        {canRemove && (
          <button type="button" className={`${big} border border-surface-border text-ink-muted hover:bg-surface-sunken`} data-testid="remove-upload-btn" onClick={() => setDialog('remove')}>
            <Trash2 className="w-5 h-5" /> Remove upload
          </button>
        )}
      </div>
      {error && !dialog && <p className="text-sm text-danger" data-testid="approve-error">{error}</p>}

      <ConfirmDialog open={dialog === 'approve'} title="Approve this invoice?" testid="approve-dialog" busy={busy}
        confirmLabel="Approve and update prices" onCancel={close}
        confirmDisabled={!!invoice.duplicate_of && !reason.trim()}
        onConfirm={() => send('approve', invoice.duplicate_of ? { override_duplicate_reason: reason.trim() } : {})}>
        {rows.some((r) => !r.history) && <p>These catalogue prices will change:</p>}
        {rows.some((r) => !r.history) && <PriceChangeTable rows={rows.filter((r) => !r.history)} testid="approve-preview-table" />}
        {rows.some((r) => r.history) && (
          <p data-testid="approve-history-note" className="text-sm">
            {rows.every((r) => r.history) ? 'No catalogue price changes: ' : 'Also: '}
            {rows.filter((r) => r.history).length} item(s) have a newer price already held than this invoice
            ({invoice.date}), so they are recorded as history and today's price stays:{' '}
            {rows.filter((r) => r.history).map((r) => r.name).join(', ')}.
          </p>
        )}
        <p className="text-xs">Pack sizes (for example 10kg) are converted to a per-kg or per-g price when approved.</p>
        {invoice.duplicate_of && (
          <label className="block">
            <span className="text-danger">This looks like an invoice already received. Say why it should still be approved:</span>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} data-testid="duplicate-reason-input"
              className="mt-1 w-full rounded-lg border border-surface-border p-2 text-ink" />
          </label>
        )}
        {error && <p className="text-danger" data-testid="approve-dialog-error">{error}</p>}
      </ConfirmDialog>

      <ConfirmDialog open={dialog === 'reject' || dialog === 'hold'} testid={`${dialog}-dialog`} busy={busy}
        title={dialog === 'reject' ? 'Reject this invoice?' : 'Put this invoice on hold?'}
        confirmLabel={dialog === 'reject' ? 'Reject' : 'Hold'} danger={dialog === 'reject'} onCancel={close}
        confirmDisabled={!reason.trim()} onConfirm={() => send(dialog, { reason: reason.trim() })}>
        <label className="block">
          <span>{dialog === 'reject' ? 'Why? No prices will be written.' : 'Why? It stays waiting until you release it.'}</span>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} data-testid={`${dialog}-reason-input`}
            className="mt-1 w-full rounded-lg border border-surface-border p-2 text-ink" />
        </label>
        {error && <p className="text-danger">{error}</p>}
      </ConfirmDialog>

      <ConfirmDialog open={dialog === 'remove'} title="Remove this upload?" testid="remove-dialog" danger busy={busy}
        confirmLabel="Remove upload" onCancel={close} onConfirm={remove}>
        <p>This deletes the invoice, its review tasks and the stored file so the same file can be uploaded again. This cannot be undone.</p>
        {approved && <p className="text-danger" data-testid="remove-approved-warning">This invoice is approved: removing it also undoes the prices it set. An owner or admin must confirm it is them.</p>}
        {error && <p className="text-danger">{error}</p>}
      </ConfirmDialog>
      <StepUpDialog open={dialog === 'remove-step-up'} action="remove_approved_upload" testid="remove-step-up"
        summary={`Remove approved invoice ${invoice.invoice_no || ''} and undo the prices it set.`}
        perform={(token) => removeUpload(invoice.id, token)} onCancel={close}
        onDone={() => { close(); onRemoved(); }} />
    </section>
  );
}
